import { Component, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { Message } from '../../models/message.model';
import { FUSE_ASYNC_DRAWER } from 'app/projects/fuse/src/lib/services/async-drawer.token';
import { AsyncJobApiService } from 'app/services/async-job.api-service';
import { JobStatus } from '../../enums/job-status.enum';
import { Job } from '../../models/job.model';
import { JobMessageStatus } from '../../enums/job-message-status.enum';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { PageEvent } from '@angular/material/paginator';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-job-details-dialog',
    templateUrl: './job-details-dialog.component.html',
    styleUrls: ['./job-details-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class JobDetailsDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    title: string;

    @Input()
    totalCount: string;

    @Input()
    job: Job;

    @Input()
    jobMessages: any;

    JobStatus = JobStatus;
    succeededMessages = new MatTableDataSource<Message>([]);
    failedMessages = new MatTableDataSource<Message>([]);
    pendingMessages = new MatTableDataSource<Message>([]);
    messagesPagination: any = { numberOfResults: 0 };
    tabs: any[] = [];

    currentPage: number = 1;
    pageSize: number = 5;
    selectedTabIndex = 0;
    isTabLoading = false;
    isPageLoading = false;
    selectedTab: any;

    private readonly succeededColumnsWithoutReason = [
        { name: 'avatar', header: '', cellTemplate: 'avatarCell' },
        { name: 'name', header: 'GENERAL.PROPERTY.NAME', cellTemplate: 'nameCell' },
    ];

    private readonly succeededColumnsWithReason = [
        { name: 'avatar', header: '', cellTemplate: 'avatarCell' },
        { name: 'name', header: 'GENERAL.PROPERTY.NAME', cellTemplate: 'nameCell' },
        { name: 'resultInfo', header: '', cellTemplate: 'resultCell' },
    ];

    getFilteredTabs(job: Job): any {
        const baseTabs = [
            {
                label: this._translateService.instant('GENERAL.STATUS.PENDING_TOTAL', { number: job.stats.pending }),
                dataSource: this.pendingMessages,
                columns: [
                    { name: 'avatar', header: '', cellTemplate: 'avatarCell' },
                    { name: 'name', header: 'GENERAL.PROPERTY.NAME', cellTemplate: 'nameCell' },
                ],
                columnNames: ['avatar', 'name'],
                key: 'pending',
            },
            {
                label: this._translateService.instant('GENERAL.STATUS.SUCCEEDED_TOTAL', { number: job.stats.succeeded }),
                dataSource: this.succeededMessages,
                columns: this.succeededColumnsWithoutReason,
                columnNames: ['avatar', 'name'],
                key: 'succeeded',
            },
            {
                label: this._translateService.instant('GENERAL.STATUS.FAILED_TOTAL', { number: job.stats.failed }),
                dataSource: this.failedMessages,
                columns: [
                    { name: 'avatar', header: '', cellTemplate: 'avatarCell' },
                    { name: 'name', header: '', cellTemplate: 'nameCell' },
                    { name: 'errorInfo', header: 'GENERAL.PROPERTY.ERROR', cellTemplate: 'errorCell' },
                ],
                columnNames: ['avatar', 'name', 'errorInfo'],
                key: 'failed',
            },
        ];

        // Filter tabs based on job.stats values
        return baseTabs.filter((tab) => job.stats[tab.key] > 0 || job.communicatedStatus === JobStatus.Created || job.communicatedStatus === JobStatus.InProgress);
    }

    private updateSucceededTabColumns(): void {
        const succeededTab = this.tabs.find((tab) => tab.key === 'succeeded');
        if (!succeededTab) {
            return;
        }

        const hasReason = this.succeededMessages.data.some((message) => !!message.resultInfo?.message);
        if (hasReason) {
            succeededTab.columns = this.succeededColumnsWithReason;
            succeededTab.columnNames = ['avatar', 'name', 'resultInfo'];
        } else {
            succeededTab.columns = this.succeededColumnsWithoutReason;
            succeededTab.columnNames = ['avatar', 'name'];
        }
    }

    constructor(
        public dialogRef: MatDialogRef<JobDetailsDialogComponent>,
        @Inject(FUSE_ASYNC_DRAWER) public asyncJobService: AsyncJobApiService,
        private _translateService: TranslateService
    ) {
        super();
    }

    async fetchMessages(jobId: string, status: string) {
        const jobMessages = await this.asyncJobService.getJobMessages(jobId, this.currentPage, this.pageSize, status);

        switch (status) {
            case JobMessageStatus.Succeeded:
                this.succeededMessages.data = jobMessages;
                this.updateSucceededTabColumns();
                break;
            case JobMessageStatus.Failed:
                this.failedMessages.data = jobMessages;
                break;
            case JobMessageStatus.Pending:
                this.pendingMessages.data = jobMessages;
                break;
            default:
                console.warn(`Unknown status: ${status}`);
        }
    }

    onPageChange(event: PageEvent, status: string) {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex + 1;
        this.fetchMessages(this.job.id, status);
    }

    async onTabChanged(event: MatTabChangeEvent): Promise<void> {
        this.isTabLoading = true;
        this.selectedTab = this.tabs[event.index].key;

        this.currentPage = 1;
        this.messagesPagination = this.asyncJobService.messagesPagination;
        await this.fetchMessages(this.job.id, this.selectedTab);
        this.isTabLoading = false;
    }

    async ngOnInit(): Promise<void> {
        this.isPageLoading = true;
        this.selectedTab = Object.keys(this.job.stats).find((key) => this.job.stats[key] > 0);
        if (this.selectedTab) {
            this.tabs = this.getFilteredTabs(this.job);
            await this.fetchMessages(this.job.id, this.selectedTab);
        }
        this.isPageLoading = false;

        this.asyncJobService.jobStatus.pipe(takeUntil(this._unsubscribeAll)).subscribe(async (job: Job) => {
            if (job) {
                this.job = job;

                // Update existing tab labels only
                this.tabs.forEach((tab) => {
                    switch (tab.key) {
                        case 'pending':
                            tab.label = this._translateService.instant('GENERAL.STATUS.PENDING_TOTAL', { number: job.stats.pending });
                            break;
                        case 'succeeded':
                            tab.label = this._translateService.instant('GENERAL.STATUS.SUCCEEDED_TOTAL', { number: job.stats.succeeded });
                            break;
                        case 'failed':
                            tab.label = this._translateService.instant('GENERAL.STATUS.FAILED_TOTAL', { number: job.stats.failed });
                            break;
                    }
                });

                await this.fetchMessages(this.job.id, this.selectedTab);
            }
        });
    }
}
